import { isAxiosError } from 'axios'

/**
 * A one-line description of an error that carries no request or response body.
 *
 * Axios errors hold `config.data` (the request body: passwords, demographics, intake answers) and
 * `response.data`; logging the error object prints them, and Sentry keeps console output as
 * breadcrumbs. This keeps the method, the endpoint, status and message only.
 */
export function errorSummary(err: unknown): string {
  if (isAxiosError(err)) {
    const method = err.config?.method?.toUpperCase() ?? ''
    const url = endpointOnly(err.config?.url ?? '')
    const status = err.response?.status ?? 'no response'
    return `${err.name} ${method} ${url} [${status}]: ${err.message}`
  }
  if (err instanceof Error) return `${err.name}: ${err.message}`
  return String(err)
}

/**
 * The first two path segments of a request URL (e.g. `/Portal/GetUser`), without host or query.
 * PatientPortalApi puts identifiers in the path (`/Portal/GetUser/{email}`,
 * `.../{facilityKey}/{patientKey}/{caseHistoryId}`), so everything after the endpoint is dropped.
 */
function endpointOnly(url: string): string {
  let path: string
  try {
    path = new URL(url, 'http://placeholder.invalid').pathname
  } catch {
    path = url.split(/[?#]/)[0] ?? ''
  }
  const segments = path.split('/').filter(Boolean).slice(0, 2)
  return segments.length ? `/${segments.join('/')}` : ''
}

/**
 * `console.error(label, err)` without leaking PHI: the full error in dev builds, only
 * `errorSummary(err)` in production.
 */
export function logError(label: string, err: unknown): void {
  if (import.meta.env.DEV) console.error(label, err)
  else console.error(label, errorSummary(err))
}
